import { z } from "zod";
import { CONCEPT_TAGS, OPENING_IDS } from "./ids.ts";

export const openingIdSchema = z.enum(OPENING_IDS);
export const conceptTagSchema = z.enum(CONCEPT_TAGS);

export const moveRoleSchema = z.enum([
  "repertoire",
  "mainline",
  "sideline",
  "transposition",
  "mistake",
  "handoff",
]);

export const lessonPhaseSchema = z.enum(["center", "development", "kingSafety", "plan"]);

export const reviewStatusSchema = z.enum(["draft", "reviewed", "approved"]);
export const explorerPopularitySchema = z.enum(["main", "common", "sideline", "rare"]);

export const lessonMoveSchema = z.object({
  uci: z.string().min(4),
  san: z.string().min(2),
  nextPositionKey: z.string().min(8),
  role: moveRoleSchema,
  explanation: z.string().min(1),
  tradeoff: z.string().optional(),
  concepts: z.array(conceptTagSchema),
  hintSteps: z.array(z.string()).default([]),
  frequency: z.number().optional(),
  tacticalWarning: z.boolean().optional(),
  explorerPopularity: explorerPopularitySchema.optional(),
  /** The named line this move belongs to; a shared position keeps only the first line's name. */
  variationName: z.string().optional(),
  /**
   * Lesson fields for a taught move outside the main line, so a side line can
   * be coached like a main-line move: the piece or area to look at, the other
   * candidate moves, what the move sets up and what to watch for.
   */
  piece: z.string().optional(),
  candidates: z.string().optional(),
  next: z.string().optional(),
  watch: z.string().optional(),
});

export const lessonNodeSchema = z.object({
  positionKey: z.string().min(8),
  fen: z.string().min(8),
  ply: z.number().int().min(0),
  variationName: z.string().optional(),
  eco: z.string().optional(),
  phase: lessonPhaseSchema,
  objective: z.string().min(1),
  /**
   * True when the objective was written for this position. Other positions
   * carry the course-wide objective, which may not fit the board.
   */
  objectiveAuthored: z.boolean().optional(),
  explanation: z.string().min(1),
  opponentIntent: z.string().optional(),
  concepts: z.array(conceptTagSchema),
  moves: z.array(lessonMoveSchema),
  handoff: z.boolean().default(false),
  reviewStatus: reviewStatusSchema.optional(),
});

export const lessonCourseSchema = z.object({
  id: z.string().min(1),
  openingId: openingIdSchema,
  slug: z.string().min(1),
  title: z.string().min(1),
  side: z.enum(["white", "black"]),
  rootPositionKey: z.string().min(8),
  targetFullMoves: z.number().int().positive(),
  level: z.enum(["beginner", "intermediate"]),
  objectives: z.array(z.string()),
  contentVersion: z.number().int().positive(),
  nodes: z.record(z.string(), lessonNodeSchema),
  reviewStatus: reviewStatusSchema.default("draft"),
  limitedCoverage: z.boolean().default(false),
  branchPolicy: z.enum(["strict", "common", "wide"]).optional(),
});

export type MoveRole = z.infer<typeof moveRoleSchema>;
export type LessonPhase = z.infer<typeof lessonPhaseSchema>;
export type LessonMove = z.infer<typeof lessonMoveSchema>;
export type LessonNode = z.infer<typeof lessonNodeSchema>;
export type LessonCourse = z.infer<typeof lessonCourseSchema>;
export type ReviewStatus = z.infer<typeof reviewStatusSchema>;
export type ExplorerPopularity = z.infer<typeof explorerPopularitySchema>;

export const tacticalStatusSchema = z.enum(["safe", "questionable", "losing", "unknown"]);
export const engineBandSchema = z.enum(["top", "equivalent", "playable", "inferior", "unknown"]);
export const bookStatusSchema = z.enum([
  "repertoire",
  "known",
  // A move the course marks as a mistake.
  "warned",
  "transposition",
  "novel",
  "unknown",
]);
export const planFitSchema = z.enum(["advances", "neutral", "conflicts", "unknown"]);

export const moveAssessmentSchema = z.object({
  tactical: z.object({
    status: tacticalStatusSchema,
    cpLoss: z.number().optional(),
  }),
  engine: z.object({
    band: engineBandSchema,
    depth: z.number().optional(),
  }),
  book: z.object({
    status: bookStatusSchema,
    variation: z.string().optional(),
    frequency: z.number().optional(),
  }),
  plan: z.object({
    fit: planFitSchema,
    explanation: z.string().optional(),
  }),
  recommendedSan: z.string().nullable().optional(),
});

export type MoveAssessment = z.infer<typeof moveAssessmentSchema>;
export type TacticalStatus = z.infer<typeof tacticalStatusSchema>;
export type EngineBand = z.infer<typeof engineBandSchema>;
export type BookStatus = z.infer<typeof bookStatusSchema>;
export type PlanFit = z.infer<typeof planFitSchema>;
